import { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useIntegrationStore } from '@/stores/integration-store';
import type { ActivityLog, Integration, KnowledgeBaseContextSnapshot } from 'dico-types';
import type { SourceConfig } from 'dico-types';
import type {
  SlackChannel,
  DiscordChannel,
  GithubRepo
} from '@/lib/api/integration/integrationApi';
import { integrationApi } from '@/lib/api/integration/integrationApi';
import Loader from '@/components/ui/loader';
import { SourceConfigRow } from '@/components/settings/SourceConfigRow';
import { ActivityLogList } from '@/components/settings/ActivityLogList';
import { listIntegrationActivityLogs } from '@/lib/api/activity-log/activityLogApi';
import { toast } from 'sonner';
import { workspaceApi } from '@/lib/api/workspace/workspaceApi';

const CORE_API_URL = import.meta.env.VITE_CORE_API_BASE_URL ?? 'http://localhost:8080';

interface Automation {
  id: string;
  name: string;
  enabled: boolean;
}

interface SuggestedTemplate {
  id: string;
  name: string;
}

interface IntegrationDetail {
  id: string;
  name: string;
  installedName: string;
  description: string;
  icon: React.ReactNode;
  viewUrl?: string;
  docsUrl?: string;
  automations: Automation[];
  suggestedTemplates: SuggestedTemplate[];
  defaultRepository?: string;
  notifyCreator: boolean;
}

function getInstalledDisplayName(account: Integration): string {
  if (account.provider === 'slack') {
    const teamName = account.config?.teamName;
    return typeof teamName === 'string' && teamName.trim().length > 0 ? teamName : 'Slack';
  }
  if (account.provider === 'discord') {
    const guildName = account.config?.guildName;
    return typeof guildName === 'string' && guildName.trim().length > 0 ? guildName : 'Discord';
  }
  if (account.provider === 'github') {
    const repoFullName = account.config?.repoFullName;
    if (typeof repoFullName === 'string' && repoFullName.trim().length > 0) return repoFullName;
    const accountLogin = account.config?.accountLogin;
    return typeof accountLogin === 'string' && accountLogin.trim().length > 0
      ? accountLogin
      : 'GitHub';
  }
  return account.provider;
}

function buildIntegrationDetail(
  integrationId: string,
  account: Integration | undefined
): IntegrationDetail | null {
  if (!account) return null;

  if (integrationId === 'slack') {
    return {
      id: 'slack',
      name: 'Slack',
      installedName: getInstalledDisplayName(account),
      description: 'Assign tasks to your workspace directly from Slack',
      icon: <MonoIcon type="Slack" className="h-7 w-7" />,
      automations: [],
      suggestedTemplates: [],
      notifyCreator: true
    };
  }

  if (integrationId === 'discord') {
    return {
      id: 'discord',
      name: 'Discord',
      installedName: getInstalledDisplayName(account),
      description: 'Create and manage tasks from Discord channels',
      icon: <MonoIcon type="Discord" className="h-7 w-7" />,
      automations: [],
      suggestedTemplates: [],
      notifyCreator: false
    };
  }

  if (integrationId === 'github') {
    return {
      id: 'github',
      name: 'GitHub',
      installedName: getInstalledDisplayName(account),
      description: 'Sync issues and pull requests from GitHub and automate developer workflows.',
      icon: <MonoIcon type="GitDiff" className="h-7 w-7" />,
      automations: [],
      suggestedTemplates: [],
      notifyCreator: false
    };
  }

  return null;
}

type AvailableTriageDestination = {
  channelId: string;
  channelName: string;
  platform: 'slack' | 'discord';
  integrationId: string;
  accountName: string;
};

export function IntegrationDetailPage() {
  const {
    id: workspaceId,
    integrationId,
    accountId
  } = useParams<{
    id: string;
    integrationId: string;
    accountId?: string;
  }>();
  const navigate = useNavigate();

  const {
    accounts,
    sourcesByAccount,
    load,
    loadSources,
    addSource,
    updateSource,
    removeSource,
    removeAccount,
    loadState
  } = useIntegrationStore();

  useEffect(() => {
    if (workspaceId) load(workspaceId);
  }, [workspaceId, load]);

  const account = useMemo(() => {
    if (accountId) return accounts.find((a) => a.id === accountId);
    return accounts.find((a) => a.provider === integrationId);
  }, [accounts, integrationId, accountId]);

  const detail = useMemo(() => {
    const id = integrationId ?? '';
    if (account) return buildIntegrationDetail(id, account);
    if (id === 'slack') {
      return {
        id: 'slack',
        name: 'Slack',
        installedName: 'Slack',
        description: 'Assign tasks to your workspace directly from Slack',
        icon: <MonoIcon type="Slack" className="h-7 w-7" />,
        viewUrl: undefined,
        docsUrl: undefined,
        suggestedTemplates: [],
        defaultRepository: undefined,
        notifyCreator: true
      };
    }
    if (id === 'discord') {
      return {
        id: 'discord',
        name: 'Discord',
        installedName: 'Discord',
        description: 'Create and manage tasks from Discord channels',
        icon: <MonoIcon type="Discord" className="h-7 w-7" />,
        viewUrl: undefined,
        docsUrl: undefined,
        suggestedTemplates: [],
        defaultRepository: undefined,
        notifyCreator: false
      };
    }
    if (id === 'github') {
      return {
        id: 'github',
        name: 'GitHub',
        installedName: 'GitHub',
        description: 'Sync issues and pull requests from GitHub and automate developer workflows.',
        icon: <MonoIcon type="GitDiff" className="h-7 w-7" />,
        viewUrl: undefined,
        docsUrl: undefined,
        suggestedTemplates: [],
        defaultRepository: undefined,
        notifyCreator: false
      };
    }
    return null;
  }, [integrationId, account]);

  const [discordLoading, setDiscordLoading] = useState(false);

  const sources = useMemo<SourceConfig[]>(
    () => (account ? (sourcesByAccount[account.id] ?? []) : []),
    [account, sourcesByAccount]
  );

  useEffect(() => {
    if (workspaceId && account && !sourcesByAccount[account.id]) {
      loadSources(workspaceId, account.id);
    }
  }, [workspaceId, account, sourcesByAccount, loadSources]);

  useEffect(() => {
    if (!workspaceId || !account) return;
    setActivityLoading(true);
    setActivityError(null);
    listIntegrationActivityLogs(workspaceId, account.id, { limit: 50 })
      .then(setActivityLogs)
      .catch((err) =>
        setActivityError(err instanceof Error ? err.message : 'Failed to load activity')
      )
      .finally(() => setActivityLoading(false));
  }, [workspaceId, account]);

  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);

  // KB initialization state
  const [kbSnapshots, setKbSnapshots] = useState<Map<string, KnowledgeBaseContextSnapshot>>(
    new Map()
  );
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [adding, setAdding] = useState(false);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [availableSlackChannels, setAvailableSlackChannels] = useState<SlackChannel[]>([]);
  const [availableDiscordChannels, setAvailableDiscordChannels] = useState<DiscordChannel[]>([]);
  const [availableGithubRepos, setAvailableGithubRepos] = useState<GithubRepo[]>([]);

  // For triage destinations: pull channel lists from all workspace accounts
  const [allSlackChannels, setAllSlackChannels] = useState<Map<string, SlackChannel[]>>(new Map());
  const [allDiscordChannels, setAllDiscordChannels] = useState<Map<string, DiscordChannel[]>>(
    new Map()
  );

  useEffect(() => {
    if (!workspaceId || !account) return;

    // Load available sources for this integration
    const loadAvailable = async () => {
      setLoadingAvailable(true);
      try {
        if (account.provider === 'slack') {
          const res = await integrationApi.listSlackChannels(workspaceId, account.id);
          setAvailableSlackChannels(res.data ?? []);
        } else if (account.provider === 'discord') {
          const res = await integrationApi.listDiscordChannels(workspaceId, account.id);
          setAvailableDiscordChannels(res.data ?? []);
        } else if (account.provider === 'github') {
          const res = await integrationApi.listGithubRepos(workspaceId, account.id);
          setAvailableGithubRepos(res.data ?? []);
        }
      } catch {
        // ignore
      } finally {
        setLoadingAvailable(false);
      }
    };

    loadAvailable();
  }, [workspaceId, account]);

  useEffect(() => {
    if (!workspaceId) return;

    // Slack channels from all Slack accounts
    accounts
      .filter((acc) => acc.provider === 'slack')
      .forEach((slackAcc) => {
        if (!allSlackChannels.has(slackAcc.id)) {
          integrationApi
            .listSlackChannels(workspaceId, slackAcc.id)
            .then((res) => {
              setAllSlackChannels((prev) => new Map(prev).set(slackAcc.id, res.data ?? []));
            })
            .catch(() => {});
        }
      });

    // Discord channels from all Discord accounts
    accounts
      .filter((acc) => acc.provider === 'discord')
      .forEach((discordAcc) => {
        if (!allDiscordChannels.has(discordAcc.id)) {
          integrationApi
            .listDiscordChannels(workspaceId, discordAcc.id)
            .then((res) => {
              setAllDiscordChannels((prev) => new Map(prev).set(discordAcc.id, res.data ?? []));
            })
            .catch(() => {});
        }
      });
  }, [workspaceId, accounts, allSlackChannels, allDiscordChannels]);

  const availableTriageDestinations = useMemo<AvailableTriageDestination[]>(() => {
    const out: AvailableTriageDestination[] = [];

    accounts
      .filter((acc) => acc.provider === 'slack')
      .forEach((slackAcc) => {
        const cfg = slackAcc.config as Record<string, string>;
        const accountName = cfg.teamName ?? slackAcc.externalId;
        const channels = allSlackChannels.get(slackAcc.id) ?? [];
        channels
          .filter((ch) => !ch.isArchived)
          .forEach((ch) => {
            out.push({
              channelId: ch.id,
              channelName: ch.name,
              platform: 'slack',
              integrationId: slackAcc.id,
              accountName
            });
          });
      });

    accounts
      .filter((acc) => acc.provider === 'discord')
      .forEach((discordAcc) => {
        const cfg = discordAcc.config as Record<string, string>;
        const accountName = cfg.guildName ?? discordAcc.externalId;
        const channels = allDiscordChannels.get(discordAcc.id) ?? [];
        channels.forEach((ch) => {
          out.push({
            channelId: ch.id,
            channelName: ch.name,
            platform: 'discord',
            integrationId: discordAcc.id,
            accountName
          });
        });
      });

    return out;
  }, [accounts, allSlackChannels, allDiscordChannels]);

  const configuredSourceIds = useMemo(() => new Set(sources.map((s) => s.sourceId)), [sources]);

  const unconfiguredSlack = useMemo(
    () => availableSlackChannels.filter((c) => !configuredSourceIds.has(c.id) && !c.isArchived),
    [availableSlackChannels, configuredSourceIds]
  );
  const unconfiguredDiscord = useMemo(
    () => availableDiscordChannels.filter((c) => !configuredSourceIds.has(c.id)),
    [availableDiscordChannels, configuredSourceIds]
  );
  const unconfiguredRepos = useMemo(
    () => availableGithubRepos.filter((r) => !configuredSourceIds.has(r.fullName)),
    [availableGithubRepos, configuredSourceIds]
  );

  const handleAddSource = async (sourceId: string, sourceName: string) => {
    if (!workspaceId || !account) return;
    setAdding(true);
    try {
      await addSource(workspaceId, account.id, {
        sourceId,
        sourceName,
        sourceType: account.provider === 'github' ? 'repo' : 'channel',
        eventTypes: account.provider === 'github' ? ['issues', 'discussions'] : ['message'],
        ingestMode: 'realtime',
        conditions: {}
      });
    } finally {
      setAdding(false);
    }
  };

  const handleChannelChange = async (
    source: SourceConfig,
    newChannelId: string,
    newChannelName: string
  ) => {
    if (!workspaceId || !account) return;
    await removeSource(workspaceId, account.id, source.id);
    await addSource(workspaceId, account.id, {
      sourceId: newChannelId,
      sourceName: newChannelName,
      sourceType: 'channel',
      eventTypes: source.eventTypes ?? ['message'],
      ingestMode: source.ingestMode,
      digestSchedule: source.digestSchedule,
      conditions: source.conditions,
      notifyEnabled: source.notifyEnabled,
      notifyChannelId: source.notifyChannelId,
      triageDestinations: source.triageDestinations
    });
  };

  const handleBackfillSource = async (sourceId: string, daysBack: number) => {
    if (!workspaceId || !account) return;
    try {
      const result = await integrationApi.syncSource(workspaceId, account.id, sourceId, {
        daysBack
      });
      if (result.data) {
        toast.success(
          `Backfilled ${result.data.messagesSynced} messages, created ${result.data.ticketsCreated} tickets`
        );
      }
    } catch {
      toast.error('Backfill failed');
    }
  };

  const handleKBInitSource = async (sourceId: string) => {
    if (!workspaceId || !account) return;
    try {
      const result = await integrationApi.initKBForSource(workspaceId, account.id, sourceId);
      if (!result.success || !result.data) {
        toast.error(result.error ?? 'Failed to start KB initialization');
        return;
      }

      const snapshot: KnowledgeBaseContextSnapshot = {
        id: result.data.snapshotId,
        workspaceId,
        integrationId: account.id,
        snapshotType: 'source_learning',
        status: result.data.status as 'pending' | 'processing' | 'completed' | 'failed',
        createdAt: new Date().toISOString(),
        analysisSummary: null,
        sampleCount: null,
        errorMessage: null,
        generatedEntries: null,
        completedAt: null
      };

      setKbSnapshots((prev) => new Map(prev).set(sourceId, snapshot));
      toast.success('Knowledge base initialization started');
      startPollingSnapshot(result.data.snapshotId, sourceId);
    } catch (error) {
      toast.error('Failed to start KB initialization');
    }
  };

  const startPollingSnapshot = (snapshotId: string, sourceId: string) => {
    // Clear any existing polling
    stopPollingSnapshot();

    // Poll every 2 seconds
    pollingIntervalRef.current = setInterval(async () => {
      if (!workspaceId) return;
      try {
        const res = await workspaceApi.getKnowledgeBaseSnapshot(workspaceId, snapshotId);
        if (res.success && res.data) {
          setKbSnapshots((prev) => new Map(prev).set(sourceId, res.data!));

          // If completed or failed, stop polling
          if (res.data.status === 'completed') {
            stopPollingSnapshot();
            toast.success('Knowledge base initialization completed!');
          } else if (res.data.status === 'failed') {
            stopPollingSnapshot();
            toast.error(res.data.errorMessage ?? 'Knowledge base initialization failed');
          }
        }
      } catch (err) {
        // Continue polling even on errors
      }
    }, 2000);
  };

  const stopPollingSnapshot = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPollingSnapshot();
    };
  }, []);

  const startOAuth = async (provider: 'slack' | 'github' | 'discord') => {
    if (!workspaceId) return;

    if (provider === 'discord') {
      setDiscordLoading(true);
      try {
        const res = await fetch(
          `${CORE_API_URL}/api/auth/discord/invite-url?workspace_id=${workspaceId}`
        );
        const data = (await res.json()) as { url?: string; error?: string };
        if (data.url) {
          window.open(data.url);
        } else {
          toast.error(data.error ?? 'Failed to generate Discord invite URL');
        }
      } catch {
        toast.error('Failed to generate Discord invite URL');
      } finally {
        setDiscordLoading(false);
      }
      return;
    }

    const url = `${CORE_API_URL}/api/auth/${provider}?workspace_id=${workspaceId}`;
    window.open(url);
  };

  if (!detail) {
    return (
      <main className="flex flex-1 flex-col items-center justify-center">
        <p className="text-muted-foreground text-sm">
          {loadState === 'loading' ? 'Loading integration…' : 'Integration not found.'}
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="mt-3"
          onClick={() => navigate(`/workspace/${workspaceId}/settings/integrations`)}
        >
          <MonoIcon type="ArrowLeft" className="mr-1.5" />
          Back to Integrations
        </Button>
      </main>
    );
  }

  return (
    <main className="flex min-h-full flex-1 flex-col">
      {/* Header */}
      <div className="border-border flex shrink-0 items-center gap-3 border-b p-3 px-4">
        <button
          onClick={() => navigate(`/workspace/${workspaceId}/settings/integrations`)}
          className="text-muted-foreground hover:text-foreground rounded-md p-1 transition-colors"
          aria-label="Back"
        >
          <MonoIcon type="ArrowLeft" className="h-4 w-4" />
        </button>
      </div>

      <div className="w-3xl mx-auto min-h-full flex-1 px-6 py-8 xl:border-x">
        {/* Integration header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="border-border bg-background flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border">
              {detail.icon}
            </div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">{detail.installedName}</h1>
              <p className="text-muted-foreground text-sm">{detail.description}</p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {!account &&
              (detail.id === 'slack' || detail.id === 'github' || detail.id === 'discord') && (
                <Button
                  size="sm"
                  onClick={() => startOAuth(detail.id as 'slack' | 'github' | 'discord')}
                  disabled={detail.id === 'discord' && discordLoading}
                >
                  {detail.id === 'discord' && discordLoading ? 'Opening…' : 'Connect'}
                </Button>
              )}
            {detail.viewUrl && (
              <a
                href={detail.viewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="border-border hover:bg-muted inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors"
              >
                View in {detail.name}
                <MonoIcon type="ExternalLink" className="h-3.5 w-3.5" />
              </a>
            )}
            {detail.docsUrl && (
              <a
                href={detail.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="border-border hover:bg-muted inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors"
              >
                View docs
                <MonoIcon type="ExternalLink" className="h-3.5 w-3.5" />
              </a>
            )}
            {account && (
              <>
                {(account.provider === 'slack' ||
                  account.provider === 'github' ||
                  account.provider === 'discord') && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => startOAuth(account.provider as 'slack' | 'github' | 'discord')}
                    disabled={account.provider === 'discord' && discordLoading}
                  >
                    {account.provider === 'discord' && discordLoading ? 'Opening…' : 'Reconnect'}
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    className={cn(buttonVariants({ variant: 'outline', size: 'icon-sm' }))}
                  >
                    <MonoIcon type="MoreHorizontal" className="h-4 w-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={async () => {
                        if (!workspaceId || !account) return;
                        const ok = window.confirm(`Disconnect ${detail.name}?`);
                        if (!ok) return;
                        try {
                          await removeAccount(workspaceId, account.id);
                          navigate(`/workspace/${workspaceId}/settings/integrations`, {
                            replace: true
                          });
                        } catch {
                          // ignore
                        }
                      }}
                    >
                      <MonoIcon type="Trash" />
                      Disconnect
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>

        {/* Automations */}
        {account && (
          <section className="mb-6">
            <div className="border-border rounded-lg border">
              <div className="border-border flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Sources</span>
                  <span className="text-muted-foreground text-xs">{sources.length}</span>
                </div>
              </div>

              <div className="flex flex-col gap-2 p-2 pt-0">
                {sources.length > 0 && (
                  <div className="flex flex-col gap-2">
                    {sources.map((source) => {
                      const snapshot = kbSnapshots.get(source.id);
                      const isKBInitializing =
                        snapshot &&
                        (snapshot.status === 'pending' || snapshot.status === 'processing');

                      return (
                        <div key={source.id} className="flex flex-col gap-1">
                          <SourceConfigRow
                            source={source}
                            workspaceId={workspaceId!}
                            accountId={account.id}
                            provider={account.provider}
                            availableChannels={availableSlackChannels}
                            availableDiscordChannels={availableDiscordChannels}
                            availableTriageDestinations={availableTriageDestinations}
                            onUpdate={async (sourceId, patch) => {
                              if (!workspaceId) return;
                              await updateSource(workspaceId, account.id, sourceId, patch);
                            }}
                            onRemove={async (sourceId) => {
                              if (!workspaceId) return;
                              await removeSource(workspaceId, account.id, sourceId);
                            }}
                            onBackfill={handleBackfillSource}
                            onChannelChange={async (newChannelId, newChannelName) => {
                              await handleChannelChange(source, newChannelId, newChannelName);
                            }}
                            onKBInit={handleKBInitSource}
                            kbInitializing={isKBInitializing}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {loadingAvailable && (
                  <div className="text-muted-foreground flex items-center gap-2 text-xs">
                    <Loader className="mr-1" />
                  </div>
                )}

                {/* Add sources */}
                {account.provider === 'slack' && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      tooltip={
                        unconfiguredSlack.length === 0 &&
                        availableSlackChannels.length > 0 &&
                        !loadingAvailable
                          ? 'All channels configured'
                          : availableSlackChannels.length === 0 && !loadingAvailable
                            ? 'No channels found'
                            : undefined
                      }
                      disabled={adding || unconfiguredSlack.length === 0}
                      onClick={() => {
                        if (unconfiguredSlack.length > 0) {
                          handleAddSource(unconfiguredSlack[0].id, unconfiguredSlack[0].name);
                        }
                      }}
                    >
                      <MonoIcon type="Plus" className="mr-1" />
                      Add channel
                    </Button>
                  </div>
                )}

                {account.provider === 'discord' && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      tooltip={
                        unconfiguredDiscord.length === 0 &&
                        availableDiscordChannels.length > 0 &&
                        !loadingAvailable
                          ? 'All channels configured'
                          : availableDiscordChannels.length === 0 && !loadingAvailable
                            ? 'No channels found'
                            : undefined
                      }
                      disabled={adding || unconfiguredDiscord.length === 0}
                      onClick={() => {
                        if (unconfiguredDiscord.length > 0) {
                          handleAddSource(unconfiguredDiscord[0].id, unconfiguredDiscord[0].name);
                        }
                      }}
                    >
                      <MonoIcon type="Plus" className="mr-1" />
                      Add channel
                    </Button>
                  </div>
                )}

                {account.provider === 'github' && (
                  <div className="flex flex-col gap-2">
                    <p className="text-muted-foreground text-xs font-medium">Add repository</p>
                    {unconfiguredRepos.length ? (
                      <div className="flex flex-wrap gap-1.5">
                        {unconfiguredRepos.map((repo) => (
                          <Button
                            key={repo.fullName}
                            variant="outline"
                            size="xs"
                            disabled={adding}
                            onClick={() => handleAddSource(repo.fullName, repo.fullName)}
                            className="h-auto gap-1 px-2 py-1 text-xs"
                          >
                            <MonoIcon type="Plus" />
                            {repo.fullName}
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-xs">
                        {availableGithubRepos.length ? 'All repos configured' : 'No repos found'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Activity */}
        {account && (
          <section className="mb-6">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-sm font-medium">Activity</span>
            </div>
            <ActivityLogList
              logs={activityLogs}
              accounts={accounts}
              workspaceId={workspaceId ?? ''}
              hideIntegrationButton
              loading={activityLoading}
              error={activityError}
            />
          </section>
        )}
      </div>
    </main>
  );
}
